import { useContext, useState, useRef, useEffect } from "react";

import { Default } from "../../components/Default/Default";
import { useAssortmentViewModel } from "../../viewModel/useAssortmentViewModel";
import { useSeetingsViewModel } from "../../viewModel/useSettingsViewModel";

import {
  FinancialSection,
  ProductPerformanceTab,
  EstimatesTab,
  HistoricalTab,
} from "./components/assortment.index";

import { LoadingContent } from "../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../components/SharedComponents/LoadingContent/loading.content.constants";

import { TenantContext } from "../../templates/context/TenantContext";

import { ASSORTMENT_INDUSTRY_TABS, USER_ROLES } from "../../components/components.constants";
import { SYSTEM_COLORS } from "../../components/system.colors";

import { i18n } from "../../translate/i18n";
import { textFormatter } from "../../utils/formatter/formatter";

const { t } = i18n;

const titlesAndLabels = {
  tabs: {
    historicalAnalyses: textFormatter(t("historicalAnalyses")),
    performanceByProduct: textFormatter(t("performanceByProduct")),
    collectionOverview: textFormatter(t("collectionOverview")),
    matrix: textFormatter(t("rovitexMatrix")),
    futurePerformance: textFormatter(t("performanceFuture"))
  },
  titles: {
    industryProductMix: textFormatter(t("industryProductMix"))
  },
  text: {
    analysisBasedMessage: textFormatter(t("analysisBasedMessage"))
  }
};

const TABS = [
  {
    key: 'historical-analysis',
    label: titlesAndLabels?.tabs?.historicalAnalyses,
    type: 'dropdown',
    options: [
      { key: ASSORTMENT_INDUSTRY_TABS.PRODUCT_PERFORMANCE, label: titlesAndLabels?.tabs?.performanceByProduct },
      { key: ASSORTMENT_INDUSTRY_TABS.HISTORICAL_FINANCE, label: titlesAndLabels?.tabs?.collectionOverview },
      { key: ASSORTMENT_INDUSTRY_TABS.HISTORICAL_MATRIX, label: titlesAndLabels?.tabs?.matrix },
    ],
  },
  { key: ASSORTMENT_INDUSTRY_TABS.PERFORMANCE_PREDICTION, label: titlesAndLabels?.tabs?.futurePerformance },
];

export function AssortmentScreen() {
  const { hierarchy } = useContext(TenantContext);

  const vmSettings = useSeetingsViewModel({ shouldFetchUserCtx: true });
  const userRole = vmSettings?.ctx?.userRole;
  const isRoleResolved = userRole != null;
  const isFashionDesigner = userRole === USER_ROLES.FASHION_DESIGNER;

  const [activeTab, setActiveTab] = useState(ASSORTMENT_INDUSTRY_TABS.HISTORICAL_FINANCE);

  const currentTab = isFashionDesigner ? ASSORTMENT_INDUSTRY_TABS.PRODUCT_PERFORMANCE : activeTab;

  const vm = useAssortmentViewModel({
    activeTab: currentTab,
    shouldFetchHistoricalMatrix: isRoleResolved && currentTab === ASSORTMENT_INDUSTRY_TABS.HISTORICAL_MATRIX,
    shouldFetchFinancialSummry: isRoleResolved && currentTab === ASSORTMENT_INDUSTRY_TABS.HISTORICAL_FINANCE,
    shouldFetchProductPerformanceSummary: isRoleResolved && currentTab === ASSORTMENT_INDUSTRY_TABS.PRODUCT_PERFORMANCE,
    shouldFetchCollectionPrediction: isRoleResolved && currentTab === ASSORTMENT_INDUSTRY_TABS.PERFORMANCE_PREDICTION
  });

  const [isHistoricalMenuOpen, setIsHistoricalMenuOpen] = useState(false);
  const historicalMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (historicalMenuRef.current && !historicalMenuRef.current.contains(event.target)) {
        setIsHistoricalMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const historicalGroup = TABS.find(t => t.type === 'dropdown');
  const isHistoricalActive = historicalGroup?.options?.some(opt => opt.key === activeTab) ?? false;

  if (!isRoleResolved)
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>;

  if (vm.uiState.isLoadingFinancialSummaryData)
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>;

  if (vm.uiState.isLoadingHistoricalMatrixData)
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>;

  if (vm.uiState.isLoadingProductPerformanceSummaryData)
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>;

  if (vm.uiState.isLoadingCollectionPredictionData)
    return <LoadingContent label={"Carregando dados..."} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>;

  return (
    <Default>
      <div style={{
        padding: "16px 28px",
        backgroundColor: SYSTEM_COLORS.WHITE.ALABASTER,
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        boxSizing: "border-box",
        width: "100%",
      }}>
        <hgroup style={{ marginBottom: 14, flexShrink: 0 }}>
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: "#1a1a1a", margin: 0, letterSpacing: "-0.3px" }}>
            {titlesAndLabels?.titles?.industryProductMix}
          </h1>
          <p style={{ fontSize: "13px", color: "#999", margin: "3px 0 0" }}>
            {titlesAndLabels?.text?.analysisBasedMessage}
          </p>
        </hgroup>

        {!isFashionDesigner && (
          <div style={{ display: "flex", marginBottom: 16, flexShrink: 0, borderBottom: "1px solid #E8773A" }}>
            {TABS.map(tab => {
              if (tab.type === 'dropdown') {
                return (
                  <div key={tab.key} ref={historicalMenuRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => setIsHistoricalMenuOpen(prev => !prev)}
                      style={{
                        padding: "8px 22px", fontSize: "13px", borderRadius: "8px 8px 0 0",
                        fontWeight: isHistoricalActive ? "700" : "500", cursor: "pointer", gap: 6,
                        background: isHistoricalActive ? "#fff" : "transparent",
                        borderBottom: isHistoricalActive ? "1px solid #fff" : "1px solid #E8773A",
                        color: isHistoricalActive ? "#1a1a1a" : "#aaa",
                        zIndex: 50, marginBottom: "-1px", border: "1px solid #E8773A", position: "relative",
                        outline: "none", transition: "all 0.15s", display: 'flex', alignItems: 'center',
                      }}>
                      {tab.label}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        style={{ transform: isHistoricalMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isHistoricalMenuOpen && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 4,
                        backgroundColor: '#fff', border: '1px solid #f0f0f0', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200, zIndex: 50, overflow: 'hidden',
                      }}>
                        {tab.options.map(opt => {
                          const isSelected = activeTab === opt.key;
                          return (
                            <button
                              key={opt.key}
                              onClick={() => { setActiveTab(opt.key); setIsHistoricalMenuOpen(false); }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                width: '100%', padding: '10px 14px', fontSize: '13px', textAlign: 'left',
                                background: isSelected ? '#fff6f0' : 'transparent',
                                border: 'none', cursor: 'pointer',
                                color: isSelected ? '#E8773A' : '#333', fontWeight: isSelected ? '600' : '400',
                              }}>
                              {opt.label}
                              {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E8773A" strokeWidth="2.5">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  padding: "8px 22px", fontSize: "13px", fontWeight: isActive ? "700" : "500",
                  cursor: "pointer", background: isActive ? "#fff" : "transparent",
                  border: "1px solid #E8773A", borderBottom: isActive ? "1px solid #fff" : "1px solid #E8773A",
                  borderRadius: "8px 8px 0 0", color: isActive ? "#1a1a1a" : "#aaa",
                  marginBottom: "-1px", position: "relative", zIndex: isActive ? 1 : 0,
                  outline: "none", transition: "all 0.15s",
                }}>
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {currentTab === ASSORTMENT_INDUSTRY_TABS.PRODUCT_PERFORMANCE && (
            <ProductPerformanceTab
              actions={{
                setPpCodeFilter: vm?.actions?.setPpCodeFilter,
                setProductReportSeason: vm?.actions?.setProductReportSeason,
                setPpAdvancedFilters: vm?.actions?.setPpAdvancedFilters,
                clearPpAdvancedFilters: vm?.actions?.clearPpAdvancedFilters,
                setPpExpandedProductId: vm?.actions?.setPpExpandedProductId,
                setPdfExportProductIds: vm?.actions?.setPdfExportProductIds,
                setPdfExportSeasonId: vm?.actions?.setPdfExportSeasonId
              }}
              ctx={{
                ppFilterOptions: vm?.ctx?.ppFilterOptions,
                ppColumns: vm?.ctx?.ppColumns,
                ppProducts: vm?.ctx?.ppProducts,
                productTagData: vm?.ctx?.productTagData,
                ppExpandedProductId: vm?.ctx?.ppExpandedProductId,
                ppSeasonOptions: vm?.ctx?.ppSeasonOptions,
                pdfExportImages: vm?.ctx?.pdfExportImages,
                ppAllProducts: vm?.ctx?.ppAllProducts,
              }}
              uiState={{
                ppCodeFilter: vm?.uiState?.ppCodeFilter,
                productReportSeason: vm?.uiState?.productReportSeason,
                ppAdvancedFilters: vm?.uiState?.ppAdvancedFilters,
                isLoadingProductTag: vm?.uiState?.isLoadingProductTag,
                isLoadingPdfExportImages: vm?.uiState?.isLoadingPdfExportImages,
              }}
            />
          )}

          {currentTab === ASSORTMENT_INDUSTRY_TABS.HISTORICAL_FINANCE && (
            <FinancialSection
              actions={{
                setSelectedBrands: vm?.actions?.setSelectedBrands,
                setFinancialSeason: vm?.actions?.setFinancialSeason
              }}
              ctx={{
                financialBrands: vm?.ctx?.financialBrands,
                columns: vm?.ctx?.columns,
                financialSeasonOptions: vm?.ctx?.financialSeasonOptions,
                tableData: vm?.ctx?.tableData
              }}
              uiState={{
                selectedBrands: vm?.uiState?.selectedBrands,
                financialSeason: vm?.uiState?.financialSeason
              }}
            />
          )}

          {currentTab === ASSORTMENT_INDUSTRY_TABS.HISTORICAL_MATRIX && (
            <HistoricalTab
              actions={{
                setHistoricalFilters: vm?.actions?.setHistoricalFilters,
                clearHistoricalFilters: vm?.actions?.clearHistoricalFilters,
                setSelectedType: vm?.actions?.setSelectedType,
                setDetailedMatrixSeason: vm?.actions?.setDetailedMatrixSeason
              }}
              ctx={{
                filterOptions: vm?.ctx?.filterOptions,
                types: vm?.ctx?.types,
                indicatorData: vm?.ctx?.indicatorData,
                detailedMatrixSeasonsOptions: vm?.ctx?.detailedMatrixSeasonsOptions,
                tenantHierarchy: hierarchy
              }}
              uiState={{
                historicalFilters: vm?.uiState?.historicalFilters,
                selectedType: vm?.uiState?.selectedType,
                detailedMatrixSeason: vm?.uiState?.detailedMatrixSeason,
                userSelectedTypeId: vm?.uiState?.userSelectedTypeId
              }}
            />
          )}

          {currentTab === ASSORTMENT_INDUSTRY_TABS.PERFORMANCE_PREDICTION && (
            <EstimatesTab
              actions={{
                setEstimatesFilters: vm?.actions?.setEstimatesFilters,
                clearEstimatesFilters: vm?.actions?.clearEstimatesFilters,
                setOnlyWithSimilars: vm?.actions?.setOnlyWithSimilars,
                setSelectedProduct: vm?.actions?.setSelectedProduct
              }}
              ctx={{
                filterOptions: vm?.ctx?.performancePredictionOptions,
                products: vm?.ctx?.products,
                tenantHierarchy: hierarchy,
                productIndicatorData: vm?.ctx?.productIndicatorData,
                estimatesSeasonOptions: vm?.ctx?.estimatesSeasonOptions,
                productImageData: vm?.ctx?.productImageData,
              }}
              uiState={{
                estimatesFilters: vm?.uiState?.estimatesFilters,
                estimatesSeason: vm?.uiState?.estimatesSeason,
                selectedProduct: vm?.uiState?.selectedProduct,
                onlyWithSimilars: vm?.uiState?.onlyWithSimilars,
                userSelectedProductId: vm?.uiState?.userSelectedProductId,
                isLoadingProductImage: vm?.uiState?.isLoadingProductImage,
              }}
            />
          )}
        </div>
      </div>
    </Default>
  );
}